/**
 * Task Edit Modal Component
 *
 * Unified modal dialog for editing task properties.
 * This is THE SINGLE edit modal used throughout the entire application.
 * All features are always shown - no variations.
 * Designed to fit in one view without scrolling.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import confetti from 'canvas-confetti';
import { 
  X, 
  Calendar, 
  FolderTree, 
  ChevronUp, 
  ChevronDown, 
  Pin, 
  PinOff,
  Trash2,
  FileText,
  Check,
  History,
  RefreshCw,
  Heart,
  Loader2,
  Target,
  Tag,
} from 'lucide-react';
import { Button } from '@/components/ui';
import { usePriorityMutations, usePriorityHistory, getPriorityColor, getPriorityLabel } from '@/hooks/use-priority';
import { useTaskValues, useTaskGoals, useTaskMutations } from '@/hooks/use-tasks';
import { useActiveGoals } from '@/hooks/use-goals';
import { useValues } from '@/hooks/use-values';
import { useTopics, useTaskRelatedTopics, useTaskTopicMutations } from '@/hooks/use-topics';
import type { Task, UpdateTaskInput, TaskPriority } from '@/types';

interface TaskEditModalProps {
  task: Task;
  isOpen: boolean;
  onClose: () => void;
  onSave: (taskId: string, updates: UpdateTaskInput) => void;
  isSaving?: boolean;
  /** Called when task is completed */
  onComplete?: (taskId: string) => void;
  /** Called when task is dismissed (not mine) */
  onDismiss?: (taskId: string) => void;
  /** Called when task is converted to reference (not a task) */
  onConvertToReference?: (taskId: string) => void;
  // Deprecated - kept for backwards compatibility but ignored
  showPriorityActions?: boolean;
}

/**
 * Priority History Panel (shown as overlay)
 */
function PriorityHistoryPanel({ 
  taskId, 
  onClose 
}: { 
  taskId: string;
  onClose: () => void;
}) {
  const { data, isLoading } = usePriorityHistory(taskId, 20);

  return (
    <div className="absolute inset-0 bg-white rounded-xl z-10 flex flex-col">
      <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200">
        <h3 className="text-lg font-semibold text-stone-900 flex items-center gap-2">
          <History className="w-5 h-5" />
          Priority History
        </h3>
        <button
          onClick={onClose}
          className="p-1 text-stone-400 hover:text-stone-600 rounded-lg hover:bg-stone-100"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      
      <div className="flex-1 overflow-y-auto p-4">
        {isLoading ? (
          <p className="text-sm text-stone-500 text-center py-8">Loading...</p>
        ) : !data?.events?.length ? (
          <p className="text-sm text-stone-500 text-center py-8">No history yet</p>
        ) : (
          <div className="space-y-2">
            {data.events.map((event) => (
              <div 
                key={event.id} 
                className="text-sm flex items-center justify-between bg-stone-50 rounded-lg px-3 py-2"
              >
                <div className="flex items-center gap-3">
                  <span className={`font-bold tabular-nums w-10 text-right ${
                    event.score_delta > 0 ? 'text-green-600' : 
                    event.score_delta < 0 ? 'text-red-600' : 'text-stone-600'
                  }`}>
                    {event.score_delta > 0 ? '+' : ''}{event.score_delta.toFixed(0)}
                  </span>
                  <span className="text-stone-700">{event.event_type.replace(/_/g, ' ')}</span>
                </div>
                <span className="text-stone-400 text-xs">
                  {new Date(event.timestamp).toLocaleDateString()}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function TaskEditModal({
  task,
  isOpen,
  onClose,
  onSave,
  isSaving = false,
  onComplete,
  onDismiss,
  onConvertToReference,
}: TaskEditModalProps) {
  // Form state
  const [title, setTitle] = useState(task.title);
  const [description, setDescription] = useState(task.description ?? task.notes ?? '');
  const [priority, setPriority] = useState<TaskPriority>(task.priority);
  const [dueAt, setDueAt] = useState(task.dueAt ? task.dueAt.split('T')[0] : '');
  const [topicPath, setTopicPath] = useState(task.topicPath || '');
  
  // Title auto-resize ref
  const titleRef = useRef<HTMLTextAreaElement>(null);
  
  // Priority state (local for immediate feedback)
  const [localPriorityScore, setLocalPriorityScore] = useState(task.priorityScore ?? 50);
  const [localIsPinned, setLocalIsPinned] = useState(task.isPinned ?? false);
  const [showHistory, setShowHistory] = useState(false);
  const [isProcessingPriority, setIsProcessingPriority] = useState(false);
  
  // Priority mutations
  const { touch, pin, unpin } = usePriorityMutations();
  
  // Task values (values this task supports)
  const { data: taskValuesData, isLoading: isLoadingTaskValues } = useTaskValues(task.id);
  const { setTaskValues, setTaskGoals } = useTaskMutations();
  const { data: allValuesData, isLoading: isLoadingAllValues } = useValues(true);
  
  // Local state for selected values
  const [selectedValueIds, setSelectedValueIds] = useState<string[]>([]);
  const [valuesChanged, setValuesChanged] = useState(false);
  
  // Task goals (goals this task supports/advances)
  const { data: taskGoalsData, isLoading: isLoadingTaskGoals } = useTaskGoals(task.id);
  const { data: allGoalsData, isLoading: isLoadingAllGoals } = useActiveGoals();
  
  // Local state for selected goals
  const [selectedGoalIds, setSelectedGoalIds] = useState<string[]>([]);
  const [goalsChanged, setGoalsChanged] = useState(false);

  // Task topics (topics this task is related to)
  const { data: taskTopicsData, isLoading: isLoadingTaskTopics } = useTaskRelatedTopics(task.id);
  const { setTaskRelatedTopics } = useTaskTopicMutations();
  const { data: allTopicsData, isLoading: isLoadingAllTopics } = useTopics();
  
  // Local state for selected topics
  const [selectedTopicIds, setSelectedTopicIds] = useState<string[]>([]);
  const [topicsChanged, setTopicsChanged] = useState(false);

  // Reset form when task changes or modal opens
  useEffect(() => {
    if (isOpen) {
      setTitle(task.title);
      setDescription(task.description ?? task.notes ?? '');
      setPriority(task.priority);
      setDueAt(task.dueAt ? task.dueAt.split('T')[0] : '');
      setTopicPath(task.topicPath || '');
      setLocalPriorityScore(task.priorityScore ?? 50);
      setLocalIsPinned(task.isPinned ?? false);
      setShowHistory(false);
      setValuesChanged(false);
      setGoalsChanged(false);
      setTopicsChanged(false);
    }
  }, [task, isOpen]);

  // Initialize selected values from API data
  useEffect(() => {
    if (taskValuesData?.values && isOpen) {
      const valueIds = taskValuesData.values.map(v => v.valueId);
      setSelectedValueIds(valueIds);
    }
  }, [taskValuesData, isOpen]);

  // Initialize selected goals from API data
  useEffect(() => {
    if (taskGoalsData?.goals && isOpen) {
      const goalIds = taskGoalsData.goals.map(g => g.goalId);
      setSelectedGoalIds(goalIds);
    }
  }, [taskGoalsData, isOpen]);

  // Initialize selected topics from API data
  useEffect(() => {
    if (taskTopicsData?.topics && isOpen) {
      const topicIds = taskTopicsData.topics.map(t => t.topicId);
      setSelectedTopicIds(topicIds);
    }
  }, [taskTopicsData, isOpen]);

  // Handle value selection toggle
  const handleValueToggle = (valueId: string) => {
    setSelectedValueIds(prev => {
      const newIds = prev.includes(valueId)
        ? prev.filter(id => id !== valueId)
        : [...prev, valueId];
      setValuesChanged(true);
      return newIds;
    });
  };

  // Handle goal selection toggle
  const handleGoalToggle = (goalId: string) => {
    setSelectedGoalIds(prev => {
      const newIds = prev.includes(goalId)
        ? prev.filter(id => id !== goalId)
        : [...prev, goalId];
      setGoalsChanged(true);
      return newIds;
    });
  };

  // Handle topic selection toggle
  const handleTopicToggle = (topicId: string) => {
    setSelectedTopicIds(prev => {
      const newIds = prev.includes(topicId)
        ? prev.filter(id => id !== topicId)
        : [...prev, topicId];
      setTopicsChanged(true);
      return newIds;
    });
  };

  // Auto-resize title textarea
  useEffect(() => {
    const textarea = titleRef.current;
    if (textarea) {
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto';
      // Set height based on content, with min of 2 lines (~48px) and max of 4 lines (~96px)
      const lineHeight = 24; // approx line height in pixels
      const minHeight = lineHeight * 2;
      const maxHeight = lineHeight * 4;
      const newHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight);
      textarea.style.height = `${newHeight}px`;
    }
  }, [title]);

  // Celebration effect for completing important tasks
  const triggerCelebration = useCallback(() => {
    const count = 200;
    const defaults = { origin: { y: 0.7 }, zIndex: 9999 };

    function fire(particleRatio: number, opts: confetti.Options) {
      confetti({ ...defaults, ...opts, particleCount: Math.floor(count * particleRatio) });
    }

    fire(0.25, { spread: 26, startVelocity: 55, origin: { x: 0.2, y: 0.7 } });
    fire(0.25, { spread: 26, startVelocity: 55, origin: { x: 0.8, y: 0.7 } });
    fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8 });
    fire(0.1, { spread: 120, startVelocity: 45, shapes: ['star'], colors: ['#FFD700', '#FFA500', '#FF6347'] });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const updates: UpdateTaskInput = {};
    
    if (title !== task.title) updates.title = title.trim();
    if (description !== (task.description ?? task.notes ?? '')) updates.description = description.trim() || undefined;
    if (priority !== task.priority) updates.priority = priority;
    const currentDue = task.dueAt ? task.dueAt.split('T')[0] : '';
    if (dueAt !== currentDue) updates.dueAt = dueAt ? `${dueAt}T23:59:59Z` : null;
    if (topicPath.trim() !== (task.topicPath || '')) updates.topicPath = topicPath.trim() || undefined;

    // Save values if changed
    if (valuesChanged) {
      try {
        await setTaskValues.mutateAsync({
          taskId: task.id,
          valueIds: selectedValueIds,
          source: 'manual',
        });
      } catch (error) {
        console.error('Failed to update task values:', error);
      }
    }

    // Save goals if changed
    if (goalsChanged) {
      try {
        await setTaskGoals.mutateAsync({
          taskId: task.id,
          goalIds: selectedGoalIds,
          source: 'manual',
        });
      } catch (error) {
        console.error('Failed to update task goals:', error);
      }
    }

    // Save related topics if changed
    if (topicsChanged) {
      try {
        await setTaskRelatedTopics.mutateAsync({
          taskId: task.id,
          topicIds: selectedTopicIds,
          source: 'manual',
        });
      } catch (error) {
        console.error('Failed to update task topics:', error);
      }
    }

    if (Object.keys(updates).length > 0) {
      onSave(task.id, updates);
    } else if (valuesChanged || goalsChanged || topicsChanged) {
      // Close if only values/goals/topics changed but no other updates
      onClose();
    } else {
      onClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  };

  // Priority action handlers
  const withPriorityProcessing = async (fn: () => Promise<unknown>) => {
    setIsProcessingPriority(true);
    try { await fn(); } finally { setIsProcessingPriority(false); }
  };

  const handleBump = () => {
    setLocalPriorityScore(prev => Math.min(100, prev + 10));
    withPriorityProcessing(() => touch.mutateAsync({ taskId: task.id, bumpAmount: 10 }));
  };

  const handleDemote = () => {
    setLocalPriorityScore(prev => Math.max(0, prev - 10));
    withPriorityProcessing(() => touch.mutateAsync({ taskId: task.id, bumpAmount: -10 }));
  };

  const handleTouch = () => {
    withPriorityProcessing(() => touch.mutateAsync({ taskId: task.id }));
  };

  const handlePin = () => {
    setLocalIsPinned(true);
    withPriorityProcessing(() => pin.mutateAsync({ taskId: task.id, reason: 'Manually pinned', days: 7 }));
  };

  const handleUnpin = () => {
    setLocalIsPinned(false);
    withPriorityProcessing(() => unpin.mutateAsync(task.id));
  };

  const handleSetPriorityLevel = (level: 'high' | 'medium' | 'low') => {
    const targetScores = { high: 80, medium: 50, low: 20 };
    const targetScore = targetScores[level];
    const bumpAmount = targetScore - localPriorityScore;
    setLocalPriorityScore(targetScore);
    withPriorityProcessing(() => touch.mutateAsync({ taskId: task.id, bumpAmount }));
  };

  const handleComplete = () => {
    if (onComplete) {
      if (localIsPinned || localPriorityScore >= 70) triggerCelebration();
      onComplete(task.id);
    }
  };

  const handleDismiss = () => { if (onDismiss) onDismiss(task.id); };
  const handleConvertToReference = () => { if (onConvertToReference) onConvertToReference(task.id); };

  if (!isOpen) return null;

  const priorityColorClass = getPriorityColor(localPriorityScore);
  const priorityLabel = getPriorityLabel(localPriorityScore);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onKeyDown={handleKeyDown}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal - wider layout */}
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-3xl overflow-hidden" data-testid="task-edit-modal">
        {/* History overlay */}
        {showHistory && (
          <PriorityHistoryPanel taskId={task.id} onClose={() => setShowHistory(false)} />
        )}

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-stone-200 bg-stone-50">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-stone-900">Edit Task</h2>
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-bold ${priorityColorClass}`}>
              {localIsPinned && <Pin className="w-3.5 h-3.5 fill-current" />}
              <span className="tabular-nums">{Math.round(localPriorityScore)}</span>
              <span className="text-xs opacity-75">({priorityLabel})</span>
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-stone-400 hover:text-stone-600 rounded-lg hover:bg-stone-200 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content - Two column layout */}
        <form onSubmit={handleSubmit}>
          <div className="p-5">
            {/* Title - Full width at top, auto-resizes */}
            <div className="mb-4">
              <label htmlFor="task-title" className="block text-xs font-medium text-stone-600 mb-1">
                Title
              </label>
              <textarea
                ref={titleRef}
                id="task-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                placeholder="Task title"
                required
                autoFocus
                style={{ overflow: 'hidden', lineHeight: '1.5rem', minHeight: '3rem' }}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Left column - Basic fields */}
              <div className="space-y-3">
                {/* Description */}
                <div>
                  <label htmlFor="task-description" className="block text-xs font-medium text-stone-600 mb-1">
                    Description
                  </label>
                  <textarea
                    id="task-description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                    placeholder="Optional description..."
                    rows={2}
                  />
                </div>

                {/* Due Date & Topic - inline */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="task-due" className="block text-xs font-medium text-stone-600 mb-1">
                      <Calendar className="w-3 h-3 inline mr-1" />Due
                    </label>
                    <div className="flex gap-1">
                      <input
                        id="task-due"
                        type="date"
                        value={dueAt}
                        onChange={(e) => setDueAt(e.target.value)}
                        className="flex-1 px-2 py-1.5 text-sm border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                      {dueAt && (
                        <button
                          type="button"
                          onClick={() => setDueAt('')}
                          className="p-1.5 text-stone-400 hover:text-red-500 rounded"
                          title="Clear"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div>
                    <label htmlFor="task-priority" className="block text-xs font-medium text-stone-600 mb-1">
                      Base Priority
                    </label>
                    <select
                      id="task-priority"
                      value={priority}
                      onChange={(e) => setPriority(e.target.value as TaskPriority)}
                      className="w-full px-2 py-1.5 text-sm border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
                    >
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>
                  </div>
                </div>

                {/* Topic */}
                <div>
                  <label htmlFor="task-topic" className="block text-xs font-medium text-stone-600 mb-1">
                    <FolderTree className="w-3 h-3 inline mr-1" />Topic
                  </label>
                  <input
                    id="task-topic"
                    type="text"
                    value={topicPath}
                    onChange={(e) => setTopicPath(e.target.value)}
                    className="w-full px-3 py-1.5 text-sm border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="e.g., Work/Projects/Name"
                  />
                </div>

                {/* Values this task supports */}
                <div>
                  <label className="block text-xs font-medium text-stone-600 mb-1">
                    <Heart className="w-3 h-3 inline mr-1" />Values Supported
                    {valuesChanged && <span className="ml-1 text-primary-500">(unsaved)</span>}
                  </label>
                  {isLoadingAllValues || isLoadingTaskValues ? (
                    <div className="flex items-center gap-2 text-xs text-stone-400 py-2">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Loading values...
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-1.5 max-h-20 overflow-y-auto">
                      {allValuesData?.values?.map((value) => {
                        const isSelected = selectedValueIds.includes(value.id);
                        return (
                          <button
                            key={value.id}
                            type="button"
                            onClick={() => handleValueToggle(value.id)}
                            className={`
                              px-2 py-0.5 text-xs rounded-full border transition-colors
                              ${isSelected
                                ? 'bg-primary-100 border-primary-300 text-primary-700'
                                : 'bg-white border-stone-200 text-stone-600 hover:border-primary-200 hover:bg-primary-50'
                              }
                            `}
                            title={value.description || value.name}
                          >
                            {isSelected && <span className="mr-0.5">✓</span>}
                            {value.name}
                          </button>
                        );
                      })}
                      {(!allValuesData?.values || allValuesData.values.length === 0) && (
                        <span className="text-xs text-stone-400">No values defined</span>
                      )}
                    </div>
                  )}
                </div>

                {/* Goals this task supports */}
                <div>
                  <label className="block text-xs font-medium text-stone-600 mb-1">
                    <Target className="w-3 h-3 inline mr-1" />Goals Supported
                    {goalsChanged && <span className="ml-1 text-primary-500">(unsaved)</span>}
                  </label>
                  {isLoadingAllGoals || isLoadingTaskGoals ? (
                    <div className="flex items-center gap-2 text-xs text-stone-400 py-2">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Loading goals...
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-1.5 max-h-20 overflow-y-auto">
                      {allGoalsData?.goals?.map((goal) => {
                        const isSelected = selectedGoalIds.includes(goal.id);
                        return (
                          <button
                            key={goal.id}
                            type="button"
                            onClick={() => handleGoalToggle(goal.id)}
                            className={`
                              px-2 py-0.5 text-xs rounded-full border transition-colors
                              ${isSelected
                                ? 'bg-emerald-100 border-emerald-300 text-emerald-700'
                                : 'bg-white border-stone-200 text-stone-600 hover:border-emerald-200 hover:bg-emerald-50'
                              }
                            `}
                            title={goal.description || goal.title}
                          >
                            {isSelected && <span className="mr-0.5">✓</span>}
                            {goal.title}
                          </button>
                        );
                      })}
                      {(!allGoalsData?.goals || allGoalsData.goals.length === 0) && (
                        <span className="text-xs text-stone-400">No active goals</span>
                      )}
                    </div>
                  )}
                </div>

                {/* Topics this task is related to */}
                <div>
                  <label className="block text-xs font-medium text-stone-600 mb-1">
                    <Tag className="w-3 h-3 inline mr-1" />Related Topics
                    {topicsChanged && <span className="ml-1 text-primary-500">(unsaved)</span>}
                  </label>
                  {isLoadingAllTopics || isLoadingTaskTopics ? (
                    <div className="flex items-center gap-2 text-xs text-stone-400 py-2">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Loading topics...
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-1.5 max-h-20 overflow-y-auto">
                      {allTopicsData?.topics?.map((topic) => {
                        const isSelected = selectedTopicIds.includes(topic.id);
                        return (
                          <button
                            key={topic.id}
                            type="button"
                            onClick={() => handleTopicToggle(topic.id)}
                            className={`
                              px-2 py-0.5 text-xs rounded-full border transition-colors
                              ${isSelected
                                ? 'bg-blue-100 border-blue-300 text-blue-700'
                                : 'bg-white border-stone-200 text-stone-600 hover:border-blue-200 hover:bg-blue-50'
                              }
                            `}
                            title={topic.description || topic.name}
                          >
                            {isSelected && <span className="mr-0.5">✓</span>}
                            {topic.name}
                          </button>
                        );
                      })}
                      {(!allTopicsData?.topics || allTopicsData.topics.length === 0) && (
                        <span className="text-xs text-stone-400">No topics defined</span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Right column - Priority & Actions */}
              <div className="space-y-3">
                {/* Dynamic Priority */}
                <div className="bg-stone-50 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-stone-600">
                      Dynamic Priority
                      {localIsPinned && <span className="ml-1 text-amber-600">📌</span>}
                    </span>
                    <button
                      type="button"
                      onClick={() => setShowHistory(true)}
                      className="text-xs text-stone-500 hover:text-primary-600 flex items-center gap-1"
                    >
                      <History className="w-3 h-3" />
                      History
                    </button>
                  </div>
                  
                  {/* Quick Set */}
                  <div className="flex gap-1.5 mb-2">
                    <button
                      type="button"
                      onClick={() => handleSetPriorityLevel('high')}
                      disabled={isProcessingPriority}
                      className="flex-1 py-1 text-xs font-medium rounded border border-orange-200 text-orange-600 hover:bg-orange-50 disabled:opacity-50"
                    >
                      High
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSetPriorityLevel('medium')}
                      disabled={isProcessingPriority}
                      className="flex-1 py-1 text-xs font-medium rounded border border-yellow-200 text-yellow-600 hover:bg-yellow-50 disabled:opacity-50"
                    >
                      Medium
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSetPriorityLevel('low')}
                      disabled={isProcessingPriority}
                      className="flex-1 py-1 text-xs font-medium rounded border border-blue-200 text-blue-600 hover:bg-blue-50 disabled:opacity-50"
                    >
                      Low
                    </button>
                  </div>

                  {/* Priority Actions */}
                  <div className="flex flex-wrap gap-1.5">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleTouch}
                      disabled={isProcessingPriority}
                      className="gap-1 text-xs h-7 px-2"
                      title="Reset decay"
                    >
                      <RefreshCw className={`w-3 h-3 ${isProcessingPriority ? 'animate-spin' : ''}`} />
                      Touch
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleBump}
                      disabled={isProcessingPriority}
                      className="gap-1 text-xs h-7 px-2 text-green-600 hover:bg-green-50"
                    >
                      <ChevronUp className="w-3 h-3" />
                      Bump
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleDemote}
                      disabled={isProcessingPriority}
                      className="gap-1 text-xs h-7 px-2 text-stone-500"
                    >
                      <ChevronDown className="w-3 h-3" />
                      Demote
                    </Button>
                    {localIsPinned ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleUnpin}
                        disabled={isProcessingPriority}
                        className="gap-1 text-xs h-7 px-2 text-amber-600"
                      >
                        <PinOff className="w-3 h-3" />
                        Unpin
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handlePin}
                        disabled={isProcessingPriority}
                        className="gap-1 text-xs h-7 px-2"
                      >
                        <Pin className="w-3 h-3" />
                        Pin
                      </Button>
                    )}
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="bg-stone-50 rounded-lg p-3">
                  <span className="text-xs font-medium text-stone-600 block mb-2">Quick Actions</span>
                  <div className="flex flex-wrap gap-1.5">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleComplete}
                      disabled={!onComplete}
                      className="gap-1 text-xs h-7 px-2 text-green-600 hover:bg-green-50 disabled:opacity-50"
                    >
                      <Check className="w-3 h-3" />
                      Done
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleConvertToReference}
                      disabled={!onConvertToReference}
                      className="gap-1 text-xs h-7 px-2 text-blue-600 hover:bg-blue-50 disabled:opacity-50"
                    >
                      <FileText className="w-3 h-3" />
                      Not a Task
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleDismiss}
                      disabled={!onDismiss}
                      className="gap-1 text-xs h-7 px-2 text-red-600 hover:bg-red-50 disabled:opacity-50"
                    >
                      <Trash2 className="w-3 h-3" />
                      Remove
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-stone-200 bg-stone-50">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-1.5 text-sm font-medium text-stone-700 hover:bg-stone-200 rounded-lg transition-colors"
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50"
              disabled={isSaving || !title.trim()}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
